/**
 * External-auth API client.
 *
 * IMPORTANT: this module DOES NOT route through services/api/client.ts. Doing so
 * would clear localStorage.monksheets_auth_token on any 401 (see client.ts:28),
 * which would log out the internal Google-SSO user whenever an external visitor
 * hits /api/external/me without a session. We use fetch() directly with
 * credentials:'include' so the HttpOnly session cookie is sent, but no internal
 * token state is touched.
 *
 * The admin surface (services/api/externalUsers.ts) MAY use client.ts because
 * its 401 handling is correct for admin sessions.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface ExternalUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Phase 105 (D-01/D-02): the `/me` response only. `ExternalUser` above is
 * left narrow because `login()` and `acceptInvite()` also return it and
 * their backend responses do not carry `salesViewer` — widening
 * `ExternalUser` itself would make those two functions' declared types lie.
 */
export interface ExternalMe extends ExternalUser {
  salesViewer: boolean;
}

export class ExternalApiError extends Error {
  statusCode: number;
  errorCode?: string;
  retryAfter?: number;

  constructor(opts: {
    statusCode: number;
    message: string;
    errorCode?: string;
    retryAfter?: number;
  }) {
    super(opts.message);
    this.name = 'ExternalApiError';
    this.statusCode = opts.statusCode;
    this.errorCode = opts.errorCode;
    this.retryAfter = opts.retryAfter;
  }
}

interface ExternalErrorPayload {
  error?: string;
  message?: string;
  retryAfter?: number;
}

async function externalFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const payload: ExternalErrorPayload = await res
      .json()
      .catch(() => ({ error: 'UNKNOWN', message: res.statusText }));
    const retryAfterHeader = res.headers.get('Retry-After');
    throw new ExternalApiError({
      statusCode: res.status,
      errorCode: typeof payload.error === 'string' ? payload.error : undefined,
      message:
        typeof payload.message === 'string'
          ? payload.message
          : (payload.error ?? 'Unknown error'),
      retryAfter:
        typeof payload.retryAfter === 'number'
          ? payload.retryAfter
          : retryAfterHeader && /^\d+$/.test(retryAfterHeader)
            ? Number(retryAfterHeader)
            : undefined,
    });
  }
  return (await res.json()) as T;
}

// =====================================================================
// Endpoint wrappers — one per Phase 14 public route + Phase 15 GET /me
// =====================================================================

export async function login(input: { email: string; password: string }): Promise<ExternalUser> {
  const res = await externalFetch<{ data: ExternalUser }>('POST', '/external/login', input);
  return res.data;
}

export async function logout(): Promise<void> {
  await externalFetch<{ data: { received: true } }>('POST', '/external/logout');
}

export async function getMe(): Promise<ExternalMe> {
  const res = await externalFetch<{ data: ExternalMe }>('GET', '/external/me');
  return res.data;
}

/**
 * Phase 25 Plan 25-06 — alias of `getMe` used by the new
 * `useExternalCurrentUser` hook (mounted by `TaskDetailSlideOver` — formerly
 * `TaskDetailModal`, replaced in Phase 41 Plan 41-17 — for the
 * external-audience comment surface).
 *
 * IMPORTANT: this is intentionally a thin wrapper around `getMe()` — it
 * MUST keep going through `externalFetch` (NOT `client.apiGet`). The
 * plan snippet originally proposed routing through `apiGet`, but doing
 * so would clobber `localStorage.monksheets_auth_token` on any 401 (see
 * `client.ts:27-31` — every 401 clears the internal-SSO token). That
 * cross-context bleed is the exact landmine this module's header
 * comment was written to prevent. So Plan 25-06 reuses `getMe()` and
 * exposes it under the planner-named export.
 *
 * Backend response shape (`backend/src/api/schemas/externalAuth.ts`
 * `meResponseSchema`) declares `firstName: string` / `lastName: string`
 * (non-nullable), so this client-side type matches.
 */
export type ExternalCurrentUser = ExternalUser;
export const fetchExternalCurrentUser = getMe;

export async function forgotPassword(input: { email: string }): Promise<void> {
  await externalFetch<{ data: { received: true } }>('POST', '/external/forgot-password', input);
}

export async function resetPassword(input: { token: string; newPassword: string }): Promise<void> {
  await externalFetch<{ data: { received: true } }>('POST', '/external/reset-password', input);
}

export async function acceptInvite(input: {
  token: string;
  password: string;
}): Promise<ExternalUser> {
  const res = await externalFetch<{ data: ExternalUser }>(
    'POST',
    '/external/accept-invite',
    input,
  );
  return res.data;
}

// =====================================================================
// Error mapping (server error code → user-friendly string)
// =====================================================================

export type ErrorContext = 'login' | 'reset' | 'invite' | 'invite-modal' | 'edit-projects';

export function mapExternalAuthError(err: ExternalApiError, _context: ErrorContext): string {
  if (err.errorCode === 'INVALID_CREDENTIALS') return 'Invalid email or password.';
  if (err.errorCode === 'ACCOUNT_LOCKED' && err.retryAfter) {
    return `Too many failed attempts — try again in ${err.retryAfter} seconds.`;
  }
  if (err.errorCode === 'INVALID_TOKEN') return 'This link is invalid or has expired.';
  if (err.errorCode === 'EMAIL_ALREADY_EXISTS')
    return 'An account with this email already exists.';
  if (err.errorCode === 'INVALID_PROJECT')
    return 'One or more selected projects are no longer available. Please refresh and try again.';
  return 'Something went wrong, please try again.';
}
